import {AxiosResponse} from 'axios';
import {ActionTree, ActionContext, Action} from 'vuex';
import {AlertNotificationModuleConstant, BioProfileModuleConstant, EmployeeProfileModuleConstant, UrlModuleConstant} from '../constants';
import Deferred from '../deferred';
import BioProfileService from '../services/bioProfileService';
import BioMuleProfileService from '../services/bioProfileMuleService';
import BioAdmin from 'types/ba/bioAdmin';
import LtFramework from 'types/lt/ltFramework';

const bioProfileService: BioAdmin.IBioProfileService = new BioProfileService();
const bioMuleProfileService: BioAdmin.IBioProfileService = new BioMuleProfileService();

export const AlertNotificationActions: ActionTree<LtFramework.IAlertNotificationState, LtFramework.IRootState> = {
	open(action: ActionContext<LtFramework.IAlertNotificationState, LtFramework.IRootState>, payload: LtFramework.IAlertNotification): Promise<void> {
		return window.ef.alertNotificationStore.open(payload);
	},
	close(action: ActionContext<LtFramework.IAlertNotificationState, LtFramework.IRootState>, payload: LtFramework.IAlertNotification) {
		return window.ef.alertNotificationStore.close(payload);
	},
	synchronize(action: ActionContext<LtFramework.IAlertNotificationState, LtFramework.IRootState>, payload: LtFramework.IAlertNotification[]): Promise<boolean> {
		let alertStoreInitialized: boolean = false;
		const alertStoreDeferred: Deferred<boolean> = new Deferred();
		const alertNotifications: LtFramework.IAlertNotification[] = window.ef.alertNotificationStore.getInitialData();

		if (!alertStoreInitialized && alertNotifications) {
			action.commit(AlertNotificationModuleConstant.mutation.setAlertNotifications, alertNotifications);

			alertStoreInitialized = true;
			alertStoreDeferred.resolve(alertStoreInitialized);
		}

		window.ef.alertNotificationStore.listen((arr: LtFramework.IAlertNotification[]) => {
			if (typeof arr !== undefined) {
				action.commit(AlertNotificationModuleConstant.mutation.setAlertNotifications, arr);

				if (!alertStoreInitialized) {
					alertStoreInitialized = true;
					alertStoreDeferred.resolve(alertStoreInitialized);
				}
			}
		});

		return alertStoreDeferred.promise;
	},
};

export const BioProfileActions: ActionTree<BioAdmin.IBioProfileState, LtFramework.IRootState> = {
	getProfile(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: string): Promise<void> {
		const featureFlags = window.lt.features;
		let feature;
		if(featureFlags) {
			feature = featureFlags[featureFlags.findIndex( (x: any) => x.featureId === 'biosSpringApi')];
		}
		if(feature && feature.enabled) {
			return bioProfileService.getProfile(payload)
				.then((response: AxiosResponse<BioAdmin.IBioProfile>) => {
				action.commit(BioProfileModuleConstant.mutation.setProfile, response.data);
			});
		} else {
			return bioMuleProfileService.getProfile(payload)
				.then((response: AxiosResponse<BioAdmin.IBioProfile>) => {
					action.commit(BioProfileModuleConstant.mutation.setProfile, response.data);
				});
		}
	},
	modifyProfileClubs(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: BioAdmin.IProfileClub): void {
		action.commit(BioProfileModuleConstant.mutation.modifyProfileClubs, payload);
	},
	removeProfileClub(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: number): void {
		action.commit(BioProfileModuleConstant.mutation.removeProfileClub, payload);
	},
	updateAvatarId(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: number): void {
		action.commit(BioProfileModuleConstant.mutation.updateAvatarId, payload);
	},
	updateQuote(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: string): void {
		action.commit(BioProfileModuleConstant.mutation.updateQuote, payload);
	},
	updateQuoteCitation(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: string): void {
		action.commit(BioProfileModuleConstant.mutation.updateQuoteCitation, payload);
	},
	updateAboutMe(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: string): void {
		action.commit(BioProfileModuleConstant.mutation.updateAboutMe, payload);
	},
	modifyProfileLanguages(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: any): void {
		action.commit(BioProfileModuleConstant.mutation.modifyProfileLanguages, payload);
	},
	modifySchedule(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: any): void {
		action.commit(BioProfileModuleConstant.mutation.modifySchedule, payload);
	},
	removeProfileLanguage(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: number): void {
		action.commit(BioProfileModuleConstant.mutation.removeProfileLanguage, payload);
	},
	updateProfileSocialLink(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: any): void {
		action.commit(BioProfileModuleConstant.mutation.updateProfileSocialLink, payload);
	},
	modifyProfileServices(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: BioAdmin.IServiceTag): void {
		action.commit(BioProfileModuleConstant.mutation.modifyProfileServices, payload);
	},
	removeProfileService(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: number): void {
		action.commit(BioProfileModuleConstant.mutation.removeProfileService, payload);
	},
	modifyProfileSpecialties(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: BioAdmin.ITag): void {
		action.commit(BioProfileModuleConstant.mutation.modifyProfileSpecialties, payload);
	},
	removeProfileSpecialty(action: ActionContext<BioAdmin.IBioProfileState, LtFramework.IRootState>, payload: number): void {
		action.commit(BioProfileModuleConstant.mutation.removeProfileSpecialty, payload);
	},
};

export const EmployeeProfileActions: ActionTree<LtFramework.IEmployeeProfileState, LtFramework.IRootState> = {
	synchronize(action: ActionContext<LtFramework.IEmployeeProfileState, LtFramework.IRootState>): Promise<boolean> {
		let employeeProfileStoreInitialized: boolean = false;
		const employeeProfileStoreDeferred: Deferred<boolean> = new Deferred();
		const employeeProfile: LtFramework.IEmployeeProfile = window.ef.employeeProfileStore.getInitialData();

		if (!employeeProfileStoreInitialized && employeeProfile && employeeProfile.initialized) {
			action.commit(EmployeeProfileModuleConstant.mutation.setEmployeeProfile, employeeProfile);

			employeeProfileStoreInitialized = true;
			employeeProfileStoreDeferred.resolve(employeeProfileStoreInitialized);
		}

		window.ef.employeeProfileStore.listen((obj: LtFramework.IEmployeeProfile) => {
			if (obj) {
				action.commit(EmployeeProfileModuleConstant.mutation.setEmployeeProfile, obj);

				if (!employeeProfileStoreInitialized && obj && obj.initialized) {
					employeeProfileStoreInitialized = true;
					employeeProfileStoreDeferred.resolve(employeeProfileStoreInitialized);
				}
			}
		});

		return employeeProfileStoreDeferred.promise;
	},
};

export const UrlActions: ActionTree<LtFramework.IUrlState, LtFramework.IRootState> = {
	synchronize(action: ActionContext<LtFramework.IUrlState, LtFramework.IRootState>): Promise<boolean> {
		let urlStoreInitialized: boolean = false;
		const urlStoreDeferred: Deferred<boolean> = new Deferred();
		const url: LtFramework.IUrl = window.ef.urlStore.getInitialData();

		if (!urlStoreInitialized && url && url.initialized) {
			action.commit(UrlModuleConstant.mutation.setUrl, url);

			urlStoreInitialized = true;
			urlStoreDeferred.resolve(urlStoreInitialized);
		}

		window.ef.urlStore.listen((obj: LtFramework.IUrl) => {
			if (obj) {
				action.commit(UrlModuleConstant.mutation.setUrl, obj);

				if (!urlStoreInitialized && obj && obj.initialized) {
					urlStoreInitialized = true;
					urlStoreDeferred.resolve(urlStoreInitialized);
				}
			}
		});

		return urlStoreDeferred.promise;
	},
};
